import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import AppLayout from '../components/layout/AppLayout';
import Seo from '../components/Seo';
import { listVaults, getVaultData, getVaultChartDirect } from '../lib/api';
import { useTranslation } from 'react-i18next';

const ITEMS_PER_PAGE = 5;

/* ── Token logos ── */
const VAULT_EXPOSURE_MAP = {
  btc: [{ symbol: 'WBTC',  logo: '/wrapped-bitcoin-wbtc-icon.svg' }],
  eth: [
    { symbol: 'stETH', logo: '/stETH_ug10fg.svg' },
    { symbol: 'WETH',  logo: '/weth-logotic.png' },
    { symbol: 'WBTC',  logo: '/wrapped-bitcoin-wbtc-icon.svg' },
    { symbol: 'cbBTC', logo: '/cbbtc.png' },
  ],
};
function getExposureTokens(name = '') {
  return name.toLowerCase().includes('btc') ? VAULT_EXPOSURE_MAP.btc : VAULT_EXPOSURE_MAP.eth;
}

/* ── Risk ── */
function riskColor(level) {
  if (level <= 2) return '#22C55E';
  if (level >= 4) return '#EF4444';
  return '#F59E0B';
}

/* ── Formatters ── */
function fmtPct(v) { return v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }
function fmtAum(v) {
  if (!v) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function transformVault(data) {
  const { vault, perf } = data;
  const toPrice = p => ({ t: p?.t ?? 0, v: p?.share_price ?? p?.v ?? 0 });
  const toAum   = p => ({ t: p?.t ?? 0, v: p?.total_aum   ?? 0 });
  const s7d    = (data.chart7d  ?? []).map(toPrice);
  const s30d   = (data.chart30d ?? []).map(toPrice);
  const sAum   = (data.chart30d ?? []).map(toAum);
  return {
    id: vault.id,
    name: vault.name,
    providerLogo: vault.provider_logo_url ?? '/SVG-05.svg',
    riskLevel: vault.risk_level ?? null,
    sharePrice: perf?.share_price ?? 1,
    perf1D:  perf?.perf_1d_pct  ?? 0,
    perf7D:  perf?.perf_7d_pct  ?? 0,
    perf30D: perf?.perf_30d_pct ?? 0,
    aum: perf?.total_aum ?? 0,
    depositors: perf?.depositors ?? 0,
    spark7d:  s7d,
    spark30d: s30d,
    sparkAum: sAum,
  };
}

/* ── ECharts mini sparkline for highlight cards ── */
function HighlightSparkline({ data, color }) {
  const option = useMemo(() => {
    if (!data || data.length < 2) return null;
    const vals = data.map(p => (typeof p === 'number' ? p : (p?.v ?? 0)));
    if (vals.length < 2) return null;
    const pts = vals.map((v, i) => [i, v]);
    return {
      animation: false,
      grid: { top: 6, bottom: 0, left: 0, right: 0, containLabel: false },
      xAxis: { type: 'value', show: false, boundaryGap: false, min: 0, max: vals.length - 1 },
      yAxis: { type: 'value', show: false, scale: true, position: 'left', splitLine: { show: false } },
      series: [{
        type: 'line', data: pts, smooth: 0.35, symbol: 'none',
        lineStyle: { color, width: 2 },
        areaStyle: {
          color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: color + 'AA' }, { offset: 1, color: color + '00' }],
          },
        },
      }],
    };
  }, [data, color]);

  if (!option) return null;
  return (
    <ReactECharts option={option} style={{ width: '100%', height: '100%', display: 'block' }}
      opts={{ renderer: 'canvas', width: 'auto' }} notMerge />
  );
}

/* ── Highlight strip — 3 tall cards ── */
function HighlightStrip({ vaults }) {
  const navigate = useNavigate();
  const { t } = useTranslation('vault');
  if (!vaults.length) return null;

  const best30D    = [...vaults].sort((a, b) => b.perf30D - a.perf30D)[0];
  const best7D     = [...vaults].sort((a, b) => b.perf7D  - a.perf7D)[0];
  const largestAum = [...vaults].sort((a, b) => b.aum     - a.aum)[0];

  const cards = [
    { label: t('bestPerf30D'), vault: best30D,    metric: fmtPct(best30D?.perf30D),  positive: (best30D?.perf30D ?? 0) >= 0,  color: (best30D?.perf30D ?? 0) >= 0 ? '#22C55E' : '#EF4444', sparkData: best30D?.spark30d },
    { label: t('bestPerf7D'),  vault: best7D,     metric: fmtPct(best7D?.perf7D),    positive: (best7D?.perf7D  ?? 0) >= 0,  color: (best7D?.perf7D  ?? 0) >= 0 ? '#22C55E' : '#EF4444', sparkData: best7D?.spark7d  },
    { label: t('largestAum'),  vault: largestAum, metric: fmtAum(largestAum?.aum),   positive: true, color: '#6366f1', metricColor: '#6366f1', sparkData: largestAum?.sparkAum },
  ];

  return (
    <div className="strat-highlights">
      {cards.map((card, i) => (
        <div key={i} className="strat-highlight-card" onClick={() => navigate(`/vault/${card.vault.id}`)}>
          <div className="strat-hl-top">
            <span className="strat-hl-label">{card.label}</span>
            <div className="strat-hl-name-metric-row">
              <div className="strat-hl-name-row">
                <img src={card.vault.providerLogo} alt="" className="strat-hl-logo" />
                <span className="strat-hl-name">{card.vault.name}</span>
              </div>
              <span className={`strat-hl-metric ${card.positive ? 'pos' : 'neg'}`}
                style={card.metricColor ? { color: card.metricColor } : undefined}>
                {card.metric}
              </span>
            </div>
          </div>
          <div className="strat-hl-chart">
            <HighlightSparkline data={card.sparkData} color={card.color} />
            <div className="strat-hl-chart-fade" />
            <button className="strat-hl-cta" onClick={e => { e.stopPropagation(); navigate(`/vault/${card.vault.id}`); }}>
              {t('view')} <i className="ph-bold ph-arrow-right" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Vault row ── */
function VaultCard({ vault }) {
  const navigate = useNavigate();
  const pos1D  = vault.perf1D  >= 0;
  const pos7D  = vault.perf7D  >= 0;
  const pos30D = vault.perf30D >= 0;

  return (
    <div className="strat-card" onClick={() => navigate(`/vault/${vault.id}`)}>
      <div className="strat-id">
        <img src={vault.providerLogo} alt="" className="strat-logo" />
        <span className="strat-name">{vault.name}</span>
      </div>
      <div className="strat-asset-col">
        <img src="/usd-coin-usdc-logo.png" alt="USDC" className="strat-exp-logo" title="USDC" />
        <span className="strat-asset-label">USDC</span>
      </div>
      <div className="strat-exposure-col">
        {getExposureTokens(vault.name).map(t => (
          <img key={t.symbol} src={t.logo} alt={t.symbol} className="strat-exp-logo" title={t.symbol} />
        ))}
      </div>
      <span className={`strat-col-val strat-d-perf ${pos1D  ? 'strat-pos' : 'strat-neg'}`}>{fmtPct(vault.perf1D)}</span>
      <span className={`strat-col-val strat-d-perf ${pos7D  ? 'strat-pos' : 'strat-neg'}`}>{fmtPct(vault.perf7D)}</span>
      <span className={`strat-col-val strat-d-perf ${pos30D ? 'strat-pos' : 'strat-neg'}`}>{fmtPct(vault.perf30D)}</span>
      <span className="strat-col-val strat-d-perf">{fmtAum(vault.aum)}</span>
      <span className="strat-col-val strat-d-perf">{vault.sharePrice != null ? `$${vault.sharePrice.toFixed(2)}` : '—'}</span>
      <span className="strat-risk-col strat-d-risk">
        <span className="strat-risk-score">
          {vault.riskLevel != null ? `${vault.riskLevel}/5` : '—'}
        </span>
      </span>
      <div className="strat-mobile-row">
        <span className={`strat-mobile-kpi-val ${pos7D ? 'strat-pos' : 'strat-neg'}`}>{fmtPct(vault.perf7D)}</span>
        <span className={`strat-mobile-kpi-val ${pos30D ? 'strat-pos' : 'strat-neg'}`}>{fmtPct(vault.perf30D)}</span>
        <span className="strat-mobile-kpi-val">{vault.sharePrice != null ? `$${vault.sharePrice.toFixed(2)}` : '—'}</span>
      </div>
      <button className="strat-cta" onClick={e => { e.stopPropagation(); navigate(`/vault/${vault.id}`); }}>
        <span className="strat-cta-text">View </span><i className="ph-bold ph-arrow-right" />
      </button>
    </div>
  );
}

/* ── Skeleton ── */
function SkeletonRow() {
  return (
    <div className="strat-card strat-card-skel">
      <div className="strat-id">
        <div className="skel-block" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="skel-block" style={{ width: 140, height: 15 }} />
          <div className="skel-block" style={{ width: 80, height: 10 }} />
        </div>
      </div>
      {[0,1,2,3,4,5,6].map(i => <div key={i} className="skel-block" style={{ width: 52, height: 16 }} />)}
      <div className="skel-block strat-d-risk" style={{ width: 10, height: 10, borderRadius: '50%' }} />
      <div className="skel-block" style={{ width: 54, height: 28, borderRadius: 'var(--r-sm)' }} />
    </div>
  );
}

/* ── Sort icon ── */
function SortIcon({ col, sortBy, sortDir }) {
  if (sortBy !== col) return <i className="ph-bold ph-arrows-down-up strat-sort-icon-neutral" />;
  return <i className={`ph-bold ${sortDir === 'desc' ? 'ph-arrow-down' : 'ph-arrow-up'} strat-sort-icon-active`} />;
}

/* ── Pagination (matches VaultPage activity design) ── */
function Pagination({ total, page, onChange }) {
  const pages = Math.ceil(total / ITEMS_PER_PAGE);
  if (pages < 1) return null;
  const visible = new Set([0, pages - 1, page, page - 1, page + 1].filter(p => p >= 0 && p < pages));
  const sorted  = [...visible].sort((a, b) => a - b);
  const items   = [];
  let prev = -1;
  for (const p of sorted) {
    if (p - prev > 1) items.push('…');
    items.push(p);
    prev = p;
  }
  return (
    <div className="strat-pagination">
      <button className="strat-page-btn strat-page-nav" disabled={page === 0} onClick={() => onChange(page - 1)}>‹</button>
      {items.map((item, i) =>
        item === '…'
          ? <span key={`e${i}`} className="strat-page-ellipsis">…</span>
          : <button key={item} className={`strat-page-btn ${item === page ? 'active' : ''}`} onClick={() => onChange(item)}>{item + 1}</button>
      )}
      <button className="strat-page-btn strat-page-nav" disabled={page === pages - 1} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

/* ── Main ── */
export default function Strategies() {
  const { t } = useTranslation('vault');
  const [vaults, setVaults]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('7D');
  const [sortDir, setSortDir]   = useState('desc');
  const [riskFilter, setRiskFilter] = useState('all');
  const [aumMin, setAumMin]     = useState('');
  const [aumMax, setAumMax]     = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const filterRef = useRef(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const vaultList = await listVaults();
        const results = await Promise.all(vaultList.map(async v => {
          const [data, chart30d, chart7d] = await Promise.all([
            getVaultData(v.id, null, '1M'),
            getVaultChartDirect(v.id, '1M'),
            getVaultChartDirect(v.id, '1W'),
          ]);
          if (!data) return null;
          return transformVault({ ...data, chart30d, chart7d });
        }));
        setVaults(results.filter(Boolean));
      } catch (err) { console.error('Strategies load error:', err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  /* Close filter panel on outside click */
  useEffect(() => {
    if (!filterOpen) return;
    function onOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [filterOpen]);

  /* Reset page on filter/sort change */
  useEffect(() => { setCurrentPage(0); }, [search, riskFilter, aumMin, aumMax, sortBy, sortDir]);

  const handleSort = useCallback((col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(col); setSortDir('desc'); }
  }, [sortBy]);

  const RISKS = [
    { key: 'all',    label: 'All'    },
    { key: 'low',    label: 'Low'    },
    { key: 'medium', label: 'Medium' },
    { key: 'high',   label: 'High'   },
  ];

  const hasActiveFilters = riskFilter !== 'all' || aumMin !== '' || aumMax !== '';

  function clearFilters() {
    setRiskFilter('all');
    setAumMin('');
    setAumMax('');
  }

  const SORT_COLS = [
    { key: '1D',    label: '1D'         },
    { key: '7D',    label: '7D'         },
    { key: '30D',   label: '30D'        },
    { key: 'aum',   label: 'AUM'        },
    { key: 'price', label: t('price')   },
  ];

  const filtered = useMemo(() => {
    const minAum = aumMin !== '' ? parseFloat(aumMin) : null;
    const maxAum = aumMax !== '' ? parseFloat(aumMax) : null;
    const mult = sortDir === 'asc' ? -1 : 1;
    return vaults
      .filter(v => {
        if (search && !v.name.toLowerCase().includes(search.toLowerCase())) return false;
        if (riskFilter === 'low'    && !(v.riskLevel <= 2)) return false;
        if (riskFilter === 'medium' && !(v.riskLevel === 3)) return false;
        if (riskFilter === 'high'   && !(v.riskLevel >= 4)) return false;
        if (minAum !== null && v.aum < minAum) return false;
        if (maxAum !== null && v.aum > maxAum) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === '1D')    return mult * (b.perf1D    - a.perf1D);
        if (sortBy === '7D')    return mult * (b.perf7D    - a.perf7D);
        if (sortBy === '30D')   return mult * (b.perf30D   - a.perf30D);
        if (sortBy === 'aum')   return mult * (b.aum       - a.aum);
        if (sortBy === 'price') return mult * (b.sharePrice - a.sharePrice);
        return 0;
      });
  }, [vaults, search, riskFilter, aumMin, aumMax, sortBy, sortDir]);

  const paginated = filtered.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

  const itemListLd = useMemo(() => {
    if (!vaults.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'G12 Labs DeFi Vaults',
      description: 'Actively managed DeFi vaults on Ethereum.',
      numberOfItems: vaults.length,
      itemListElement: vaults.slice(0, 20).map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://app.g12labs.com/vault/${v.id}`,
        name: v.name,
      })),
    };
  }, [vaults]);

  return (
    <AppLayout>
      <Seo
        title="G12 Labs — Curated DeFi Vaults on Ethereum"
        description="Browse actively managed DeFi vaults by G12 Labs. Real-time APY, AUM, risk scores. Non-custodial strategies on Ethereum."
        path="/"
        jsonLd={itemListLd}
      />
      <div className="strat-page">

        {/* ── Header (title + subtitle only) ── */}
        <div className="strat-header">
          <div className="strat-header-left">
            <h1 className="strat-title">{t('strategies')}</h1>
            <p className="strat-subtitle">{t('subtitle')}</p>
          </div>
        </div>

        {/* ── Highlights strip ── */}
        {!loading && vaults.length > 0 && <HighlightStrip vaults={vaults} />}

        {/* ── Controls bar — outside the container ── */}
        <div className="strat-controls-bar">

          {/* Filter button + dropdown */}
          <div className="strat-filter-wrap" ref={filterRef}>
            <button
              className={`strat-filter-toggle ${filterOpen ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
              onClick={() => setFilterOpen(o => !o)}
            >
              <i className="ph-bold ph-funnel" />
              {t('filters')}
              {hasActiveFilters && <span className="strat-filter-badge" />}
            </button>

            {filterOpen && (
              <div className="strat-filter-panel">
                {/* Risk */}
                <div className="sfp-section">
                  <span className="sfp-label">{t('risk_label')}</span>
                  <div className="sfp-pills">
                    {RISKS.map(r => (
                      <button key={r.key} className={`sfp-pill ${riskFilter === r.key ? 'active' : ''}`}
                        onClick={() => setRiskFilter(r.key)}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* AUM range */}
                <div className="sfp-section">
                  <span className="sfp-label">{t('aumRange')}</span>
                  <div className="sfp-range-row">
                    <input type="number" className="sfp-input" placeholder="Min $"
                      value={aumMin} onChange={e => setAumMin(e.target.value)} min="0" />
                    <span className="sfp-range-sep">—</span>
                    <input type="number" className="sfp-input" placeholder="Max $"
                      value={aumMax} onChange={e => setAumMax(e.target.value)} min="0" />
                  </div>
                </div>

                {hasActiveFilters && (
                  <button className="sfp-clear" onClick={clearFilters}>{t('clearAll')}</button>
                )}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="strat-search-wrap strat-search-inline">
            <i className="ph-bold ph-magnifying-glass" />
            <input type="text" className="strat-search" placeholder={t('searchPlaceholder')}
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

        </div>

        {/* ── Vault section container ── */}
        <div className="strat-vault-section">

          {/* ── Sortable column headers — first row, dark bg ── */}
          <div className="strat-col-headers">
            <span className="strat-col-hd-plain">{t('vault')}</span>
            <span className="strat-col-hd-plain">{t('asset')}</span>
            <span className="strat-col-hd-plain">{t('exposure')}</span>

            {SORT_COLS.map(col => (
              <button key={col.key}
                className={`strat-col-sort strat-d-perf ${sortBy === col.key ? 'active' : ''}`}
                onClick={() => handleSort(col.key)}>
                {col.label}
                <SortIcon col={col.key} sortBy={sortBy} sortDir={sortDir} />
              </button>
            ))}

            <span className="strat-d-risk strat-col-hd-plain">{t('risk_label')}</span>
            <span />
          </div>

          {/* ── Mobile column headers ── */}
          <div className="strat-mobile-col-header">
            <span>Vault</span>
            <div className="strat-mobile-col-right">
              {[{ key: '7D', label: '7D' }, { key: '30D', label: '30D' }, { key: 'price', label: 'Price' }].map(col => (
                <button key={col.key}
                  className={`strat-col-sort ${sortBy === col.key ? 'active' : ''}`}
                  style={{ width: 52, justifyContent: 'center' }}
                  onClick={() => handleSort(col.key)}>
                  {col.label}
                  <SortIcon col={col.key} sortBy={sortBy} sortDir={sortDir} />
                </button>
              ))}
            </div>
            <span />
          </div>

          {/* ── Table body ── */}
          <div className="strat-vault-body">

            {/* ── List ── */}
            <div className="strat-list">
              {loading
                ? [0,1,2].map(i => <SkeletonRow key={i} />)
                : paginated.length === 0
                  ? <div className="strat-empty"><i className="ph-bold ph-magnifying-glass" /><span>{t('noMatch')}</span></div>
                  : paginated.map(vault => <VaultCard key={vault.id} vault={vault} />)
              }
            </div>

            {/* ── Pagination ── */}
            <Pagination total={filtered.length} page={currentPage} onChange={setCurrentPage} />

          </div>{/* /strat-vault-body */}
        </div>{/* /strat-vault-section */}

      </div>
    </AppLayout>
  );
}
